import React from 'react';

function Explanations() {
  return (
    <div className="explanations-wrapper">
      <div className="explanations">
        <h1>Sådan virker Boligmarkedsbarometeret</h1>
        
        <section>
          <h2>Hvorfor lave et boligbarometer?</h2>
          <p>
            Da jeg var i proces med at sælge mit hus, og nu i gang med at købe et nyt, søgte jeg et overblik over udviklingen i boligpriser, salgstal, salgstider osv. Samtidigt ønskede jeg at få lidt makro perspektiv på renteudviklingen og den økonomiske udvikling i Danmark. 
            <br />
            <br />
            Jeg skulle læse mange artikler og gennemgå en god mængde data på Danmarks Statisitk og Finans Danmark for at få et samlet billede.
            <br />
            <br />
            Så jeg besluttede at lave et værktøj som gav et samlet overblik over de forskellige indikatorer, og som gav et overblik over hvordan de forskellige indikatorer påvirker hinanden. Derudover var det et sjovt eksperiment at bruge Cursor AI og lære at arbejde med API'erne.
            <br />
            <br />
            Vise det sandheden? Tja, det vil altid være en subjektiv sandhed da vægtningen af datapunkterne kan laves på mange måder. Konstruktiv feedback er meget velkommen.
            <br />
            <br />
            Mange hilsner,
            <br />
            Jarl Rosenlykke

          </p>
        </section>

        <section>
          <h2>Boligmarkedsbarometer</h2>
          <p>
            Boligmarkedsbarometeret viser om det nuværende marked er præget af frygt, neutralitet eller grådighed. 
            Barometeret går fra 0-100%, hvor:
          </p>
          <ul>
            <li><strong>Under 45%:</strong> Indikerer et marked præget af frygt (købers marked). 
            Her ses typisk faldende priser, længere liggetider og større prisforhandlinger.</li>
            
            <li><strong>45-55%:</strong> Indikerer et neutralt og balanceret marked. 
            Her er der typisk stabile priser og en god balance mellem købere og sælgere.</li>
            
            <li><strong>Over 55%:</strong> Indikerer et marked præget af grådighed (sælgers marked). 
            Her ses typisk stigende priser, korte liggetider og begrænset rum for prisforhandling.</li>
          </ul>
          <p>
            Barometeret beregnes ved at analysere de seneste fire kvartaler, hvor nyere data vægter tungere end ældre:
          </p>
          <ul>
            <li>Seneste kvartal: 40% vægt</li>
            <li>Forrige kvartal: 30% vægt</li>
            <li>To kvartaler tilbage: 20% vægt</li>
            <li>Tre kvartaler tilbage: 10% vægt</li>
          </ul>
          <p>
            Hver indikator bidrager forskelligt til den samlede score baseret på deres betydning for markedet:
          </p>
          <ul>
            <li><strong>Salgspris og Rente:</strong> 25% hver (størst betydning)</li>
            <li><strong>Prisafslag:</strong> 15%</li>
            <li><strong>Antal solgte boliger:</strong> 12.5%</li>
            <li><strong>Liggetid og Boliger til salg:</strong> 5% hver</li>
            <li><strong>BNP og Inflation:</strong> 5% hver</li>
            <li><strong>Ledighed:</strong> 2.5%</li>
          </ul>
          <p>
            Hver indikator tildeles point fra 0-3 baseret på deres værdi:
          </p>
          <ul>
            <li><strong>Salgspris og antal solgte boliger:</strong>
              <ul>
                <li>3 point: Over 5% stigning</li>
                <li>2 point: 2-5% stigning</li>
                <li>1 point: -2% til 2% ændring</li>
                <li>0 point: Under -2% fald</li>
              </ul>
            </li>
            <li><strong>Liggetid og boliger til salg:</strong>
              <ul>
                <li>3 point: Over 5% fald</li>
                <li>2 point: 2-5% fald</li>
                <li>1 point: -2% til 2% ændring</li>
                <li>0 point: Over 2% stigning</li>
              </ul>
            </li>
            <li><strong>Rente:</strong>
              <ul>
                <li>3 point: Under 2%</li>
                <li>2 point: 2-4%</li>
                <li>1 point: 4-6%</li>
                <li>0 point: Over 6%</li>
              </ul>
            </li>
            <li><strong>Prisafslag:</strong>
              <ul>
                <li>3 point: Under 3.5%</li>
                <li>2 point: 3.5-5%</li>
                <li>1 point: 5-7.9%</li>
                <li>0 point: Over 7.9%</li>
              </ul>
            </li>
            <li><strong>BNP:</strong>
              <ul>
                <li>3 point: Over 2% vækst</li>
                <li>2 point: 1-2% vækst</li>
                <li>1 point: 0-1% vækst</li>
                <li>0 point: Negativ vækst</li>
              </ul>
            </li>
            <li><strong>Inflation:</strong>
              <ul>
                <li>3 point: Under 2%</li>
                <li>2 point: 2-3%</li>
                <li>1 point: 3-4%</li>
                <li>0 point: Over 4%</li>
              </ul>
            </li>
            <li><strong>Ledighed:</strong>
              <ul>
                <li>3 point: Under 3%</li>
                <li>2 point: 3-4%</li>
                <li>1 point: 4-5%</li>
                <li>0 point: Over 5%</li>
              </ul>
            </li>
          </ul>
        </section>

        <section>
          <h2>Dataforklaringer</h2>
          <ul>
            <li><strong>Salgspris:</strong> Procentvis ændring i salgspriser år-til-år</li>
            <li><strong>Solgte boliger:</strong> Procentvis ændring i antal solgte boliger år-til-år</li>
            <li><strong>Prisafslag:</strong> Gennemsnitligt prisafslag mellem udbudspris og salgspris</li>
            <li><strong>Liggetid:</strong> Procentvis ændring i den gennemsnitlige liggetid år-til-år</li>
            <li><strong>Boliger til salg:</strong> Procentvis ændring i antal boliger til salg år-til-år</li>
            <li><strong>BNP:</strong> Procentvis ændring i Danmarks Bruttonationalprodukt</li>
            <li><strong>Rente:</strong> Den aktuelle realkredit inklusiv bidragssats</li>
            <li><strong>Inflation:</strong> Den årlige inflationsrate</li>
            <li><strong>Ledighed:</strong> Den aktuelle ledighedsprocent</li>
          </ul>
        </section>

        <section>
          <h2>Hvorfor år-til-år Data - burde det ikke være indekstal?</h2>
          <p>
            Jo, det kunne jeg også have gjort. Det var dog svært at gøre let forståeligt, så jeg valgte at gå en anden vej.
          </p> 
        </section>

        <section>
          <h2>Datakilderne</h2>
          <p>
            Alle data kommer fra officielle danske kilder for at sikre høj kvalitet og pålidelighed:
          </p>
          <ul>
            <li>
              <strong>Boligmarkedsdata:</strong> Boligsiden.dk's markedsindeks, som omfatter:
              <ul>
                <li>Salgspriser</li>
                <li>Antal solgte boliger</li>
                <li>Liggetider</li>
                <li>Antal boliger til salg</li>
                <li>Prisafslag</li>
              </ul>
            </li>
            <li>
              <strong>Økonomiske indikatorer:</strong>
              <ul>
                <li>BNP: Danmarks Statistik (DST)</li>
                <li>Inflation: Danmarks Statistik (DST)</li>
                <li>Ledighed: Danmarks Statistik (DST)</li>
                <li>Rente: Nationalbankens statistikbank og Finans Danmark</li>
              </ul>
            </li>
          </ul>
          <p>
            Data opdateres kvartalsvis, når nye tal offentliggøres fra de respektive kilder. 
            Nogle indikatorer som rente, inflation og ledighed opdateres månedligt og omregnes 
            til kvartalsgennemsnit for at passe ind i modellen.
          </p>
        </section>

        <section>
          <h2>Farvekoder</h2>
          <ul>
            <li><span className="best">Mørkegrøn:</span> Bedste kategori (3 point)</li>
            <li><span className="second-best">Lysegrøn:</span> Næstbedste kategori (2 point)</li>
            <li><span className="second-worst">Orange:</span> Næstdårligste kategori (1 point)</li>
            <li><span className="worst">Rød:</span> Dårligste kategori (0 point)</li>
          </ul>
        </section>

        <section>
          <h2>Eksempel på Beregning</h2>
          <p>Her er et eksempel på hvordan barometeret beregnes for én indikator (salgspris) over fire kvartaler:</p>
          
          <table className="example-calculation">
            <thead>
              <tr>
                <th>Kvartal</th>
                <th>Ændring</th>
                <th>Point</th>
                <th>Vægt</th>
                <th>Vægtet Score</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>2024 K1</td>
                <td>6.2%</td>
                <td>3 point</td>
                <td>40%</td>
                <td>1.20</td>
              </tr>
              <tr>
                <td>2023 K4</td>
                <td>3.5%</td>
                <td>2 point</td>
                <td>30%</td>
                <td>0.60</td>
              </tr>
              <tr>
                <td>2023 K3</td>
                <td>1.5%</td>
                <td>1 point</td>
                <td>20%</td>
                <td>0.20</td>
              </tr>
              <tr>
                <td>2023 K2</td>
                <td>-3.0%</td>
                <td>0 point</td>
                <td>10%</td>
                <td>0.00</td>
              </tr>
              <tr className="highlight">
                <td colSpan="4">Samlet vægtet score for salgspris:</td>
                <td>2.00</td>
              </tr>
            </tbody>
          </table>
          
          <p>
            Dette er kun for salgspris-indikatoren (25% vægt af total). 
            Den endelige barometerværdi beregnes ved at:
          </p>
          <ol>
            <li>Gentage denne proces for alle indikatorer</li>
            <li>Gange hver indikators score med dens vægt</li>
            <li>Lægge alle vægtede scores sammen</li>
            <li>Dividere med makspoint (3) og gange med 100 for at få procent</li>
          </ol>
        </section>
      </div>
    </div>
  );
}

export default Explanations; 